import {
  Box,
  createStyles,
  IconButton,
  makeStyles,
  TextField,
  Theme,
  Typography,
} from '@material-ui/core'
import React, { useContext, useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { useDispatch } from 'react-redux'
import { openSnackbar } from '../../ducks/snackbar/actions'
import circleDefaultImage from '../../assets/images/circle.png'
import { ICircle, addFileToFirebaseStorage } from 'daily-dose-common'
import { firestore } from '../../firebase-init'
import { FirestoreContext } from '../../providers/firestore-provider'
import { CustomDialogContent } from '../../components'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    input: {
      display: 'none',
    },
    large: {
      width: theme.spacing(7),
      height: theme.spacing(7),
    },
    image: {
      maxWidth: '100%',
      height: '200px',
    },
    typography: {
      align: 'center',
    },
    center: {
      alignSelf: 'center',
    },
    column: {
      flexDirection: 'column',
    },
  })
)

type Inputs = {
  name: string
  description: string
}

interface IEditCircleModalProps {
  onCancelCircle: () => void
  onUpdateCircle: () => void
  selectedCircle: ICircle
}

const EditCircleModal = ({
  onCancelCircle,
  onUpdateCircle,
  selectedCircle,
}: IEditCircleModalProps) => {
  const classes = useStyles()
  const { currentUser } = useContext(FirestoreContext)
  const { handleSubmit, errors, control } = useForm<Inputs>()

  const dispatch = useDispatch()
  const [file, setFile] = useState(({} as any) || null)

  const [imageUrl, setImageUrl] = useState(selectedCircle.photoURL || ('' as any))

  const onChangeFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0]
      const reader = new FileReader()
      reader.readAsDataURL(file) as any

      reader.onloadend = function () {
        // Picture is smaller than 10mb
        if (file.size < 10 * 1024 * 1024) {
          setFile(file)
          setImageUrl(reader.result)
        } else {
          alert('Choose a picture with less than 10MB size')
        }
      }.bind(this)
    }
  }

  const onSubmit = async (data: Inputs) => {
    const { name, description } = data

    // TODO: We might not ned this id since we're already chekcing currentUser
    const { id } = currentUser || {}
    const isUser = !!(id && currentUser)
    let photoURL = null
    if (isUser) {
      try {
        const circleRef = firestore.collection('circles').doc(selectedCircle.id!)
        // TODO: Add loading when we move dialog to redux
        if (file?.name) {
          photoURL = await addFileToFirebaseStorage('circle-profiles', id!, file)
        }

        await circleRef.update({
          name,
          description,
          photoURL: photoURL || imageUrl,
        })

        dispatch(openSnackbar(`Awesome! Your group is now updated.`, 'success'))
        onUpdateCircle()
      } catch (error) {
        console.warn('Error putting photo in bucket ', error)
        dispatch(openSnackbar('Opps. Something happened. Please try again', 'error'))
      }
    } else {
      dispatch(openSnackbar('Please add both a name and description', 'info'))
    }
  }

  return (
    <CustomDialogContent
      title="Edit Group"
      confirmText="Edit"
      onCancel={onCancelCircle}
      onSubmit={handleSubmit(onSubmit)}
    >
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="AddCircle"
        style={{
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <input
          accept="image/*"
          className={classes.input}
          id="icon-button-file"
          type="file"
          onChange={onChangeFile}
        />
        <label htmlFor="icon-button-file" className={classes.center}>
          <Box alignSelf="center">
            <IconButton color="primary" aria-label="upload picture" component="span">
              <div className={classes.column}>
                <img
                  src={imageUrl || circleDefaultImage}
                  className={classes.image}
                />
                <Typography
                  variant="body2"
                  color="inherit"
                  className={classes.typography}
                >
                  Change picture
                </Typography>
              </div>
            </IconButton>
          </Box>
        </label>

        <Controller
          as={TextField}
          name="name"
          control={control}
          rules={{ required: true }}
          defaultValue={selectedCircle.name}
          variant="outlined"
          margin="normal"
          fullWidth
          label="Name"
          autoComplete="off"
          type="text"
          placeholder="Because even the Beatles had one..."
          error={!!errors.name}
          helperText={!!errors.name && 'The name of your group is missing.'}
          InputLabelProps={{
            shrink: true,
          }}
        />

        <Controller
          as={TextField}
          multiline
          name="description"
          control={control}
          rules={{ required: false }}
          defaultValue={selectedCircle.description}
          variant="outlined"
          margin="normal"
          fullWidth
          label="Description"
          autoComplete="off"
          type="text"
          placeholder="Because why not?"
          rows={4}
          InputLabelProps={{
            shrink: true,
          }}
        />
      </form>
    </CustomDialogContent>
  )
}

export default EditCircleModal
